import { useState, useEffect, useCallback } from 'react';

export interface RaisedHand {
  participantId: string;
  participantName: string;
  raised: boolean;
  timestamp: string;
}

interface UseRaiseHandOptions {
  currentUserId: string;
  currentUserName: string;
  audioVideo: any; // AudioVideoFacade from Chime SDK
}

interface UseRaiseHandReturn {
  isHandRaised: boolean;
  raisedHands: Map<string, RaisedHand>;
  toggleHand: () => void;
  lowerHand: (participantId: string) => void;
}

const RAISE_HAND_TOPIC = 'RAISE_HAND';

export function useRaiseHand({
  currentUserId,
  currentUserName,
  audioVideo,
}: UseRaiseHandOptions): UseRaiseHandReturn {
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [raisedHands, setRaisedHands] = useState<Map<string, RaisedHand>>(new Map());

  // Subscribe to raise hand messages
  useEffect(() => {
    if (!audioVideo) return;

    const handleDataMessage = (dataMessage: any) => {
      try {
        const data = JSON.parse(dataMessage.text());
        
        if (data.type === 'RAISE_HAND') {
          const handState: RaisedHand = {
            participantId: data.participantId,
            participantName: data.participantName,
            raised: data.raised,
            timestamp: data.timestamp,
          };

          setRaisedHands((prev) => {
            const newMap = new Map(prev);
            if (handState.raised) {
              newMap.set(handState.participantId, handState);
            } else {
              newMap.delete(handState.participantId);
            }
            return newMap;
          });

          // Update local state if it's our own hand
          if (data.participantId === currentUserId) {
            setIsHandRaised(data.raised);
          }
        }
      } catch (error) {
        console.error('Error parsing raise hand message:', error);
      }
    };

    try {
      audioVideo.realtimeSubscribeToReceiveDataMessage(RAISE_HAND_TOPIC, handleDataMessage);
    } catch (error) {
      console.error('Error subscribing to raise hand messages:', error);
    }

    return () => {
      try {
        audioVideo.realtimeUnsubscribeFromReceiveDataMessage(RAISE_HAND_TOPIC);
      } catch (error) {
        console.error('Error unsubscribing from raise hand messages:', error);
      }
    };
  }, [audioVideo, currentUserId]);

  const sendHandState = useCallback(
    (participantId: string, participantName: string, raised: boolean) => {
      if (!audioVideo) return;

      try {
        const dataMessage = JSON.stringify({
          type: 'RAISE_HAND',
          participantId,
          participantName,
          raised,
          timestamp: new Date().toISOString(),
        });

        audioVideo.realtimeSendDataMessage(RAISE_HAND_TOPIC, dataMessage);
      } catch (error) {
        console.error('Error sending raise hand message:', error);
      }
    },
    [audioVideo]
  );

  const toggleHand = useCallback(() => {
    const newState = !isHandRaised;
    setIsHandRaised(newState);

    // Update local raised hands map
    setRaisedHands((prev) => {
      const newMap = new Map(prev);
      if (newState) {
        newMap.set(currentUserId, {
          participantId: currentUserId,
          participantName: currentUserName,
          raised: true,
          timestamp: new Date().toISOString(),
        });
      } else {
        newMap.delete(currentUserId);
      }
      return newMap;
    });

    // Send to other participants
    sendHandState(currentUserId, currentUserName, newState);
  }, [isHandRaised, currentUserId, currentUserName, sendHandState]);

  // Host can lower any participant's hand
  const lowerHand = useCallback(
    (participantId: string) => {
      const participant = raisedHands.get(participantId);
      if (!participant) return;

      // Update local state
      setRaisedHands((prev) => {
        const newMap = new Map(prev);
        newMap.delete(participantId);
        return newMap;
      });

      // If lowering own hand
      if (participantId === currentUserId) {
        setIsHandRaised(false);
      }

      // Send to other participants
      sendHandState(participantId, participant.participantName, false);
    },
    [raisedHands, currentUserId, sendHandState]
  );

  return {
    isHandRaised,
    raisedHands,
    toggleHand,
    lowerHand,
  };
}
